import axios from 'axios';
import router from "@/router";
import { applyTenantHeader } from '@/utils/tenant';
import { getHospital } from '@/services/inst.service';
import { extractTraceId, showTraceError } from '@/utils/trace-error';
import { appendInsuranceTrace } from '@/services/his.patient.payment';
import { setHisStatus } from '@/services/his-status.service';
import { isReadonlyPath, isWriteRequest } from '@/utils/menu-access';
import { Message } from 'element-ui';

// 默认 URL → infno 映射，业务侧未传 _ybTraceCtx.infno 时使用
// 注：标的是"主操作"infno；像 /submit-order 内部串了 2201+2203+2204，trace 是合并条，标主操作 2201 挂号
// 注：/read-card 走本地 SDK，不调医保不会有 ybApiTrace，故未列入
const URL_INFNO_MAP = {
    '/getPatientInfo': '1101',
    '/chronic-disease': '5301',
    '/submit-order': '2201',
    '/pre-settle': '2206',
    '/settle': '2207',
    '/settle-cancel': '2208',
    '/cancel-mdtrt': '2202',
    '/reversal': '2601',
    '/report/dip': '4101',
    '/pre-review': '3101',
    '/detail-review': '3102',
    '/drug-purchase': '3503',
    '/drug-sale': '3505',
    '/drug-sale-return': '3506',
    '/settlement-query': '5203',
    '/fee-detail': '5204',
    '/settle-qltctrl-query': '4104',
    '/settlement-statement/summary': '3201',
    '/settlement-statement/detail': '3202',
    '/file-upload': '9101',
    '/settlement-apply': '3203',
    '/settlement-apply/cancel': '3204'
};

// 已经在业务代码里通过 updateProgressStatusOnly(traceStr) 显式落盘 trace 的 URL，避免双写
const SKIP_AUTO_TRACE_URLS = new Set([
    '/submit-order',
    '/pre-settle',
    '/settle',
    '/settle-cancel'
]);

function resolveYbInfno(config) {
    const ctx = config && config._ybTraceCtx;
    if (ctx && ctx.infno) return String(ctx.infno);
    // /test 和 /call 用 query 里的 code
    const code = config && config.params && config.params.code;
    if (code && (config.url === '/test' || config.url === '/call')) {
        return String(code);
    }
    return URL_INFNO_MAP[config && config.url] || null;
}

// 自动落盘医保调用日志：响应里若带 ybApiTrace 字段，按上下文 fire-and-forget 调 appendInsuranceTrace
function autoUploadYbTrace(response) {
    try {
        const config = (response && response.config) || {};
        if (config._skipAutoTrace) return;
        if (SKIP_AUTO_TRACE_URLS.has(config.url)) return;
        const data = response && response.data;
        if (!data || typeof data !== 'object') return;
        const traceStr = data.ybApiTrace;
        if (!traceStr) return;
        const ctx = config._ybTraceCtx || {};
        appendInsuranceTrace({
            traceStr,
            infno: resolveYbInfno(config),
            progressId: ctx.progressId || null,
            patientId: ctx.patientId || null,
            paymentId: ctx.paymentId || null
        }).catch(() => { /* 静默：trace 上报失败不影响主流程 */ });
    } catch (e) {
        // 防御性兜底：自动上报本身异常也不能打断响应链
    }
}

let maxTime = process.env.MAX_REQ_TIME;

// 创建请求服务（不设置 baseURL，在请求拦截器中动态设置）
const serves = axios.create({
    timeout: maxTime
})

// 设置请求发送之前的拦截器
serves.interceptors.request.use(config => {
    // 当前菜单只读时，拦掉写类医保操作（挂号/预结算/结算/冲正/上传等）；
    // 读卡、查询、对账等只读接口不受影响。不加 __menu 头，避免院内 EasyHis 的跨域限制。
    const currentPath = router.currentRoute ? router.currentRoute.path : '';
    if (isReadonlyPath(currentPath) && isWriteRequest(config.url, config.method)) {
        Message.warning('当前角色对该菜单为只读权限，不可操作');
        return Promise.reject(new Error('READONLY_MENU'));
    }

    // 从医院信息中获取 baseURL（如果调用方已设置则跳过）
    const hospital = getHospital();
    if (!hospital) {
        // getHospital() 内部已经处理了跳转登录页的逻辑
        return Promise.reject(new Error('未获取到医院信息'));
    }

    if (!config.baseURL) {
        if (!hospital.hisLocalServiceUrl) {
            // 不弹消息提示，由顶栏状态点（红点）指示医保未连接
            return Promise.reject(new Error('当前医院未配置医保服务'));
        }
        // 动态设置 baseURL
        config.baseURL = hospital.hisLocalServiceUrl;
    }

    applyTenantHeader(config);
    return config
}, err => {Promise.reject(err)})

// 设置响应拦截器
serves.interceptors.response.use(response => {
    if (response.status === 200 || response.status === 204) {
        // 业务调用成功本身就是探活信号，反向把状态置为 online
        setHisStatus('online');
        autoUploadYbTrace(response);
        return Promise.resolve(response.data)
    } else {
        // 如果请求失败，则统一处理错误
        return Promise.reject(response)
    }
}, err => {
    // 只读拦截已在请求拦截器提示过，静默透传，避免重复弹框、也别误判医保离线
    if (err && err.message === 'READONLY_MENU') {
        return Promise.reject('当前角色对该菜单为只读权限，不可操作');
    }
    // 取出状态码和消息
    let message = '';
    let status = err.response && err.response.status ? err.response.status : 0;

    // 可选接口尚未上线时，由调用方结束后台任务，不弹统一错误提示。
    // 保留原始错误，不能把 404 伪装成空数据或同步成功。
    if (status === 404 && err.config && err.config._ignoreNotFound) {
        return Promise.reject(err);
    }

    let msg = err.response && err.response.data && err.response.data.error ? (
        err.response.data.error.details ? err.response.data.error.details :
        err.response.data.error.message ? err.response.data.error.message :
        err.response.data.error_description ? err.response.data.error_description :
        status + '错误') : status + '错误';

    // 提取 TraceId
    let traceId = extractTraceId(err);

    // 网络错误（hisrequest 连内网，网络错误只提示不跳登录）
    const isNetworkError = !err.response || (err.message && err.message.indexOf("Network Error") != -1);
    if (isNetworkError) {
        // 业务调用网络失败，反向把状态置为 offline，下次发起前会弹确认窗口
        setHisStatus('offline');
    }

    // 判断请求时发生的错误类型，处理不同的错误
    switch (status) {
        case 400:
            message = msg ? msg : '访问失效，请重新登陆';
            break;
        case 401:
            message = '无访问权限';
            break;
        case 406:
            message = '应用未激活或已过有效期';
            router.push('/app-register');
            break;
        case 403:
            message = msg ? msg :'请求错误';
            break;
        case 415:
            message = '参数格式错误';
            break;
        case 500:
            message = '接口发生错误';
            break;
        default:
            if (isNetworkError) {
                // 网络错误不弹消息，由顶栏状态点指示
                return Promise.reject('医保服务连接失败');
            } else {
                message = msg ? msg : `请求错误`;
            }
            break;
    }
    showTraceError(message, traceId, status);
    return Promise.reject(message)
})

export default serves
